import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { TasksTypeTemplate } from '../constants/TasksTypeTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useTaskTypesService from '../hooks/services/useTaskTypesService';
import { ruLocale } from '../lang/ruLocale';
import { TaskType } from '../types/interfaces';
import TaskTypeCard from '../ui/TaskTypeCard';
import { clearFilter, generateTags } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function TasksType() {
  const [tasksType, setTasksType] = useState<TaskType[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(1);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [tasksTypeToDelete, setTasksTypeToDelete] = useState<
    string | number | null
  >(null);
  const [selectedTasksType, setSelectedTasksType] =
    useState<TaskType>(TasksTypeTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    statusTransitionName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    planningAlgorithm: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    processingTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
    responseTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
    resolutionTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
    requiredEmployees: { value: null, matchMode: FilterMatchMode.CONTAINS },
    defaultOperations: { value: null, matchMode: FilterMatchMode.CONTAINS },
    priority: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const [loading, setLoading] = useState<boolean>(true);
  const statusTransitionFilterRef = useRef<HTMLInputElement>(null);
  const planningAlgorithmFilterRef = useRef<HTMLInputElement>(null);
  const tasksTypeFilterRef = useRef<HTMLInputElement>(null);
  const processingTimeFilterRef = useRef<HTMLInputElement>(null);
  const responseTimeFilterRef = useRef<HTMLInputElement>(null);
  const resolutionTimeFilterRef = useRef<HTMLInputElement>(null);
  const requiredEmployeesFilterRef = useRef<HTMLInputElement>(null);
  const defaultOperationsFilterRef = useRef<HTMLInputElement>(null);
  const priorityFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const tasksTypeCardRef = useRef<null | { handleCancel: () => void }>(null);

  const isSelectedIdExist = isSelected(selectedTasksType);

  const taskTypesService = useTaskTypesService();
  const getTaskTypesCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const statusTransitionFilter =
      filters.statusTransitionName as DataTableFilterMetaData;
    const planningAlgorithmFilter =
      filters.planningAlgorithm as DataTableFilterMetaData;
    const taskTypeFilter = filters.taskType as DataTableFilterMetaData;
    const processingTimeFilter =
      filters.processingTime as DataTableFilterMetaData;
    const responseTimeFilter = filters.responseTime as DataTableFilterMetaData;
    const resolutionTimeFilter =
      filters.resolutionTime as DataTableFilterMetaData;
    const requiredEmployeesFilter =
      filters.requiredEmployees as DataTableFilterMetaData;
    const defaultOperationsFilter =
      filters.defaultOperations as DataTableFilterMetaData;
    const globalFilter = filters.global as DataTableFilterMetaData;
    const priorityFilter = filters.priority as DataTableFilterMetaData;
    const serviceResult = taskTypesService.getAll({
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify([
        {
          field: 'statusTransition',
          value: statusTransitionFilter.value,
          operator: statusTransitionFilter.matchMode
        },
        {
          field: 'planningAlgorithm',
          value: planningAlgorithmFilter.value,
          operator: planningAlgorithmFilter.matchMode
        },
        {
          field: 'taskType',
          value: taskTypeFilter.value,
          operator: taskTypeFilter.matchMode
        },
        {
          field: 'processingTime',
          value: processingTimeFilter.value,
          operator: processingTimeFilter.matchMode
        },
        {
          field: 'responseTime',
          value: responseTimeFilter.value,
          operator: responseTimeFilter.matchMode
        },
        {
          field: 'resolutionTime',
          value: resolutionTimeFilter.value,
          operator: resolutionTimeFilter.matchMode
        },
        {
          field: 'requiredEmployees',
          value: requiredEmployeesFilter.value,
          operator: requiredEmployeesFilter.matchMode
        },
        {
          field: 'defaultOperations',
          value: defaultOperationsFilter.value,
          operator: defaultOperationsFilter.matchMode
        },
        {
          field: 'priority',
          value: priorityFilter.value,
          operator: priorityFilter.matchMode
        },
        {
          field: 'global',
          value: globalFilter.value,
          operator: globalFilter.matchMode
        }
      ])
    });
    serviceResult.result.then((r) => {
      if (r) {
        setTasksType(
          r.itemsList.map((x) => ({
            ...x,
            statusTransitionName: x.statusTransition?.label ?? ''
          }))
        );
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);
      }
      setLoading(false);
    });
    return () => serviceResult.cancel();
  }, [filters, multiSortMeta, taskTypesService]);

  useEffect(() => {
    const cancel = getTaskTypesCallback();
    return () => cancel();
  }, [getTaskTypesCallback]);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedTasksType(TasksTypeTemplate);
    setDialogVisible(true);
  };

  const handleDeleteConfirmation = () => {
    setSelectedTasksType(TasksTypeTemplate);
    handleDelete(tasksTypeToDelete);
    setDeleteDialogVisible(false);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const handleUpdate = async (id: string | number, updatedObject: TaskType) => {
    const result = await taskTypesService.addOrUpdate(updatedObject)?.result;
    getTaskTypesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await taskTypesService.delete(parseInt(id.toString()))?.result;
      setSelectedTasksType(TasksTypeTemplate);
      getTaskTypesCallback();
    }
  };

  const handleAdd = async (taskType: TaskType) => {
    const result = await taskTypesService.addOrUpdate(taskType)?.result;
    getTaskTypesCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideDetailTypeCard = () => {
    if (tasksTypeCardRef.current !== null) {
      tasksTypeCardRef.current.handleCancel();
    }
  };

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите тип наряда для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (selectedTasksType?.id) {
      setTasksTypeToDelete(selectedTasksType.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !selectedTasksType?.id) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите тип наряда для удаления',
        life: 3000
      });
    }
  };

  const header = (
    <TablePageHeader
      tableName="tasks-type"
      onGlobalSearchChange={(value) =>
        handleInputChange('global')({
          target: { value }
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const priorityBodyTemplate = (
    rowData: TaskType & { priorityText?: string }
  ) => {
    const priorityText =
      rowData.priority === 0
        ? 'Низкий'
        : rowData.priority === 1
        ? 'Средний'
        : rowData.priority === 2
        ? 'Высокий'
        : '';
    return <span>{priorityText}</span>;
  };

  return (
    <div className="taskType-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Тип наряда"
        visible={dialogVisible}
        style={{
          minWidth: '630px',
          width: '630px',
          minHeight: 'fit-content',
          maxHeight: '100vh'
        }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        modal
        contentStyle={{ height: 'fit-content', paddingBottom: '80px' }}
        onHide={() => onHideDetailTypeCard()}
      >
        <VisibilityWrapper code="taskType-card__roles">
          <TaskTypeCard
            tasksTypeProps={selectedTasksType}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={tasksTypeCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="тип наряда"
        entityName={selectedTasksType?.taskType}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedTasksType!}
        onSelectionChange={(e) => setSelectedTasksType(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedTasksType(e.data as TaskType);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={tasksType}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Типы нарядов не найдены. "
        onRowReorder={(e) => setTasksType(e.value)}
        tableStyle={{
          width: '100%',
          minWidth: '50rem',
          tableLayout: 'fixed'
        }}
        onFilter={onFiltersChange}
        footer={
          <div
            className="p-paginator p-component p-paginator-bottom justify-content-end"
            style={{
              height: 60
            }}
          >
            <span>{`Всего: ${tasksType.length > 0 ? totalCount : 0}`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="taskType"
          sortable
          field="taskType"
          header="Тип наряда"
          filter
          onFilterClear={() =>
            clearFilter('taskType', filters, setFilters, tasksTypeFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={tasksTypeFilterRef}
                onChange={handleInputChange('taskType')}
                placeholder="Поиск по типу наряда"
              />
            </div>
          }
          style={{ width: '13%' }}
        />
        <Column
          columnKey="statusTransitionName"
          sortable
          field="statusTransitionName"
          sortField="statusTransition"
          header="Процесс обработки наряда"
          showClearButton={true}
          filter
          onFilterClear={() =>
            clearFilter(
              'statusTransitionName',
              filters,
              setFilters,
              statusTransitionFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={statusTransitionFilterRef}
                onChange={handleInputChange('statusTransitionName')}
                placeholder="Поиск по процессам обработки наряда"
              />
            </div>
          }
          style={{ width: '13%' }}
        />
        <Column
          columnKey="planningAlgorithm"
          sortable
          field="planningAlgorithm.label"
          sortField="planningAlgorithm"
          header="Алгоритм распределения"
          showClearButton={true}
          filter
          onFilterClear={() =>
            clearFilter(
              'planningAlgorithm',
              filters,
              setFilters,
              planningAlgorithmFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={planningAlgorithmFilterRef}
                onChange={handleInputChange('planningAlgorithm')}
                placeholder="Поиск по алгоритму распределения"
              />
            </div>
          }
          style={{ width: '12%' }}
        />
        <Column
          columnKey="processingTime"
          sortable
          field="processingTime"
          header="Плановые трудозатраты (мин)"
          filter
          onFilterClear={() =>
            clearFilter(
              'processingTime',
              filters,
              setFilters,
              processingTimeFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={processingTimeFilterRef}
                onChange={handleInputChange('processingTime')}
                placeholder="Поиск по времени обработки наряда"
              />
            </div>
          }
          style={{ width: '9%' }}
        />
        <Column
          columnKey="responseTime"
          sortable
          field="responseTime"
          header="Плановый срок начала работ"
          filter
          onFilterClear={() =>
            clearFilter(
              'responseTime',
              filters,
              setFilters,
              responseTimeFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={responseTimeFilterRef}
                onChange={handleInputChange('responseTime')}
                placeholder="Поиск по плановому сроку начала работ"
              />
            </div>
          }
          style={{ width: '7%' }}
        />
        <Column
          columnKey="resolutionTime"
          sortable
          field="resolutionTime"
          header="Плановый срок завершения работ"
          filter
          onFilterClear={() =>
            clearFilter(
              'resolutionTime',
              filters,
              setFilters,
              resolutionTimeFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={resolutionTimeFilterRef}
                onChange={handleInputChange('resolutionTime')}
                placeholder="Поиск по плановому сроку завершения работ"
              />
            </div>
          }
          style={{ width: '7%' }}
        />
        <Column
          columnKey="requiredEmployees"
          sortable
          field="requiredEmployees"
          header="Треб. кол-во исполнителей"
          filter
          onFilterClear={() =>
            clearFilter(
              'requiredEmployees',
              filters,
              setFilters,
              requiredEmployeesFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={requiredEmployeesFilterRef}
                onChange={handleInputChange('requiredEmployees')}
                placeholder="Поиск по кол-ву исполнителей"
              />
            </div>
          }
          style={{ width: '7%' }}
        />

        <Column
          columnKey="priority"
          sortable
          field="priority"
          header="Приоритет"
          filter
          body={priorityBodyTemplate}
          onFilterClear={() =>
            clearFilter('priority', filters, setFilters, priorityFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={priorityFilterRef}
                onChange={handleInputChange('priority')}
                placeholder="Поиск по приоритету"
              />
            </div>
          }
          style={{ width: '5%' }}
        />
        <Column
          columnKey="defaultOperations"
          field="defaultOperations"
          header="Плановые работы"
          filter
          onFilterClear={() =>
            clearFilter(
              'defaultOperations',
              filters,
              setFilters,
              defaultOperationsFilterRef
            )
          }
          body={(taskTypeData: TaskType | undefined) =>
            generateTags(taskTypeData, 'defaultOperations')
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={defaultOperationsFilterRef}
                onChange={handleInputChange('defaultOperations')}
                placeholder="Поиск по плановым работам"
              />
            </div>
          }
          style={{ width: '27%' }}
        />
      </DataTable>
    </div>
  );
}
